import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"

import { PricingProductHeader } from "./PricingProductHeader"
import { PricingTiersTable } from "./PricingTiersTable"
import type { PricingTiersTableHandle } from "./PricingTiersTable"
import { PricingWarehouseHeader } from "./PricingWarehouseHeader"
import type { PricingGridRow } from "./types"

import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import type {
  BulkPriceChangeUpdateInput,
  PriceChangeItems,
  PriceChangeQuoteRequestProduct,
  PriceChangeType,
  Product,
  SubmitPriceChangeItemInput,
  UnitOfMeasurement,
  UpdateSubmissionItemInput,
} from "@/graphql/codegen/graphql"
import { PriceChangeTableVariant, PricingDialogs } from "@/screens/Pricing/constants"
import { useDefaultedRowKeys } from "@/screens/Pricing/hooks/useDefaultedRowKeys"
import type { PricingEditContext } from "@/screens/Pricing/hooks/usePricingTiersActions"
import type { WarehouseHeaderFormValues } from "@/screens/Pricing/schema/WarehouseHeaderSchema"
import { usePriceTableEditsStore } from "@/screens/Pricing/stores/usePriceTableEditsStore"
import { toBulkPreviewItem, toBulkPreviewRow } from "@/screens/Pricing/utils/bulkPricePreview"
import { hasPriceChangeGroupsWithItems } from "@/screens/Pricing/utils/priceChangeGroups"
import {
  calculateInitialUOM,
  toPriceChangeSubmitItems,
  toPriceChangeUpdateItems,
} from "@/screens/Pricing/utils/priceChangeSubmit"
import { getPriceChangeEditKey } from "@/screens/Pricing/utils/pricingRow"

export interface ProductPricingTableHandle {
  getSubmitItems: () => SubmitPriceChangeItemInput[]
  getUpdateItems: () => UpdateSubmissionItemInput[]
  resetInvalidDates: () => void
  reseed: () => void
  hasManualEditsInSelection: () => boolean
  flushPendingEdits: () => Promise<void>
}

interface ProductPricingTableProps {
  priceChangeItems: PriceChangeItems
  quoteProduct?: PriceChangeQuoteRequestProduct
  uoms: UnitOfMeasurement[]
  setDialogOpen: (open: PricingDialogs) => void
  setSelectedProduct: (product: Product) => void
  onSelectionChange?: (id: string, info: { selected: number; total: number }) => void
  onChangesChange?: (id: string, hasChanges: boolean) => void
  readonly?: boolean
  variant?: PriceChangeTableVariant
  isQuoteSource?: boolean
  canRemove?: boolean
  priceChangeType: PriceChangeType
  persistEdits?: boolean
  bulkUpdate?: BulkPriceChangeUpdateInput
}

interface PendingWarehouseUpdate {
  groupId: string
  values: WarehouseHeaderFormValues
  kind: "cost" | "price"
}

export const ProductPricingTable = forwardRef<ProductPricingTableHandle, ProductPricingTableProps>(
  (
    {
      priceChangeItems,
      quoteProduct,
      uoms,
      setDialogOpen,
      setSelectedProduct,
      onSelectionChange,
      onChangesChange,
      readonly,
      variant = PriceChangeTableVariant.SubmitPriceChange,
      priceChangeType,
      isQuoteSource,
      canRemove = false,
      persistEdits = false,
      bulkUpdate,
    },
    ref
  ) => {
    const tiersTableRefs = useRef<Record<string, PricingTiersTableHandle | null>>({})

    const initialUomId = useMemo(
      () => calculateInitialUOM(priceChangeItems.groups, priceChangeItems.product.packageUsWeightUom),
      [priceChangeItems.groups, priceChangeItems.product]
    )
    // On revisit, seed the selected UoM from a persisted edit for this product so the
    // header reflects the UoM the rows were edited under — not the recalculated default.
    const getSelectedUom = useCallback((): UnitOfMeasurement | undefined => {
      const persistedUomId = persistEdits
        ? Object.values(usePriceTableEditsStore.getState().edits).find(
            (edit) => edit.productId === priceChangeItems.product.productId
          )?.uomId
        : undefined
      return uoms.find((uom) => uom.id === (persistedUomId ?? initialUomId)) ?? undefined
    }, [persistEdits, priceChangeItems.product.productId, uoms, initialUomId])
    const [selectedUom, setSelectedUom] = useState<UnitOfMeasurement | undefined>(getSelectedUom)
    const isUomChanged = (selectedUom?.id ?? null) !== (initialUomId ?? null)
    const [pendingWarehouseUpdate, setPendingWarehouseUpdate] = useState<PendingWarehouseUpdate | null>(null)
    const [pendingAddPackageDialog, setPendingAddPackageDialog] = useState<PricingDialogs | null>(null)

    const [groupNewAcceptedCost, setGroupNewAcceptedCost] = useState<Record<string, number | null>>({})
    const handleGroupNewAcceptedCostChange = useCallback((groupId: string, value: number | null) => {
      setGroupNewAcceptedCost((prev) => (prev[groupId] === value ? prev : { ...prev, [groupId]: value }))
    }, [])

    const handleWarehouseUpdateApply = (pending: PendingWarehouseUpdate) => {
      const tableRef = tiersTableRefs.current[pending.groupId]
      if (pending.kind === "cost") {
        tableRef?.applyGroupCostChange(pending.values)
      } else {
        tableRef?.applyPriceChange(pending.values)
      }
      setPendingWarehouseUpdate(null)
    }

    const handleWarehousePriceChangeApply = (groupId: string, values: WarehouseHeaderFormValues) => {
      const pending: PendingWarehouseUpdate = { groupId, values, kind: "price" }
      if (tiersTableRefs.current[groupId]?.hasManualEditsInSelection()) {
        setPendingWarehouseUpdate(pending)
      } else {
        handleWarehouseUpdateApply(pending)
      }
    }

    const handleWarehouseCostChangeApply = (groupId: string, values: WarehouseHeaderFormValues) => {
      const pending: PendingWarehouseUpdate = { groupId, values, kind: "cost" }
      if (tiersTableRefs.current[groupId]?.hasAnyManualEdits()) {
        setPendingWarehouseUpdate(pending)
      } else {
        handleWarehouseUpdateApply(pending)
      }
    }

    const handleAddPackageDialogOpen = useCallback(
      (dialog: PricingDialogs) => {
        setDialogOpen(dialog)
        setSelectedProduct(priceChangeItems.product)
      },
      [setDialogOpen, setSelectedProduct, priceChangeItems.product]
    )

    const handleAddPackageClick = (dialog: PricingDialogs) => {
      const hasManualEdits = Object.values(tiersTableRefs.current).some((tableRef) => tableRef?.hasAnyManualEdits())
      if (hasManualEdits) {
        setPendingAddPackageDialog(dialog)
      } else {
        handleAddPackageDialogOpen(dialog)
      }
    }

    const handleRemoveSubmissionProduct = () => {
      setSelectedProduct(priceChangeItems.product)
      setDialogOpen(PricingDialogs.RemoveSubmissionProductConfirmation)
    }

    const handleAddPackageConfirm = () => {
      if (pendingAddPackageDialog) handleAddPackageDialogOpen(pendingAddPackageDialog)
      setPendingAddPackageDialog(null)
    }

    const initialRowsByGroup = useMemo<Record<string, PricingGridRow[]>>(() => {
      const persistedEdits = persistEdits ? usePriceTableEditsStore.getState().edits : {}
      return Object.fromEntries(
        priceChangeItems.groups.map((group) => {
          const rows = group.items.flatMap((item) => {
            const previewItem = bulkUpdate ? toBulkPreviewItem(item, bulkUpdate) : item

            const tierRows = item.tiers.map<PricingGridRow>((tier) => {
              const baseRow: PricingGridRow = {
                ...tier,
                item: previewItem,
                acceptedCost: group.acceptedCost,
                newPrice: tier.price,
                newProfit: null,
                newMargin: null,
                previousAcceptedCost: group.previousAcceptedCost,
                shipToWarehouseArea: group.shipToWarehouseArea,
              }
              const previewRow = bulkUpdate ? toBulkPreviewRow(baseRow, bulkUpdate) : baseRow

              return persistedEdits[getPriceChangeEditKey(group.id, baseRow)]?.row ?? previewRow
            })

            const canonicalItem =
              tierRows.find((row) => persistedEdits[getPriceChangeEditKey(group.id, row)])?.item ?? previewItem
            return tierRows.map((row) => (row.item === canonicalItem ? row : { ...row, item: canonicalItem }))
          })
          const rowKeys = new Set(rows.map((row) => getPriceChangeEditKey(group.id, row)))
          const addedRows = Object.values(persistedEdits)
            .filter((edit) => edit.group.id === group.id && !rowKeys.has(getPriceChangeEditKey(group.id, edit.row)))
            .map((edit) => edit.row)
          return [group.id, [...rows, ...addedRows]]
        })
      )
    }, [priceChangeItems.groups, persistEdits, bulkUpdate])

    useDefaultedRowKeys({ groups: priceChangeItems.groups, bulkUpdate, enabled: Boolean(persistEdits) })

    useImperativeHandle(
      ref,
      () => ({
        getSubmitItems: () =>
          priceChangeItems.groups.flatMap((group) => {
            const rows = tiersTableRefs.current[group.id]?.getSubmitRowData() ?? []
            return toPriceChangeSubmitItems(
              rows,
              priceChangeItems.product.productId,
              group,
              selectedUom?.id ?? "",
              priceChangeType,
              groupNewAcceptedCost[group.id]
            )
          }),

        getUpdateItems: () =>
          priceChangeItems.groups.flatMap((group) => {
            const rows = tiersTableRefs.current[group.id]?.getUpdateRowData() ?? []
            return toPriceChangeUpdateItems(rows, group, selectedUom?.id ?? "", groupNewAcceptedCost[group.id])
          }),
        resetInvalidDates: () => {
          Object.values(tiersTableRefs.current).forEach((tableRef) => tableRef?.resetInvalidDates())
        },
        reseed: () => {
          setSelectedUom(getSelectedUom())
          setPendingWarehouseUpdate(null)
          setPendingAddPackageDialog(null)
          setGroupNewAcceptedCost({})
          setGroupSelectedCounts({})
          setGroupChanges({})
          priceChangeItems.groups.forEach((group) => {
            tiersTableRefs.current[group.id]?.reseed(initialRowsByGroup[group.id] ?? [])
          })
        },
        hasManualEditsInSelection: () =>
          Object.values(tiersTableRefs.current).some((tableRef) => tableRef?.hasManualEditsInSelection()),
        flushPendingEdits: async () => {
          await Promise.all(Object.values(tiersTableRefs.current).map((tableRef) => tableRef?.flushPendingEdits()))
        },
      }),
      [
        priceChangeItems.groups,
        priceChangeItems.product.productId,
        priceChangeType,
        selectedUom?.id,
        groupNewAcceptedCost,
        getSelectedUom,
        initialRowsByGroup,
      ]
    )

    const tableId = priceChangeItems.id
    const [groupSelectedCounts, setGroupSelectedCounts] = useState<Record<string, number>>({})
    const handleGroupSelectionChange = useCallback((groupId: string, info: { selected: number; total: number }) => {
      setGroupSelectedCounts((prev) => (prev[groupId] === info.selected ? prev : { ...prev, [groupId]: info.selected }))
    }, [])
    const totalSelected = Object.values(groupSelectedCounts).reduce((acc, count) => acc + count, 0)
    useEffect(() => {
      onSelectionChange?.(tableId, { selected: totalSelected, total: totalSelected })
    }, [totalSelected, onSelectionChange, tableId])

    const [groupChanges, setGroupChanges] = useState<Record<string, boolean>>({})
    const handleGroupChangesChange = useCallback((groupId: string, hasChanges: boolean) => {
      setGroupChanges((prev) => (prev[groupId] === hasChanges ? prev : { ...prev, [groupId]: hasChanges }))
    }, [])
    const hasAnyChanges = Object.values(groupChanges).some(Boolean)
    useEffect(() => {
      onChangesChange?.(tableId, hasAnyChanges)
    }, [hasAnyChanges, onChangesChange, tableId])

    const editContextByGroup = useMemo<Record<string, PricingEditContext | undefined>>(() => {
      if (!persistEdits) return {}
      return Object.fromEntries(
        priceChangeItems.groups.map((group) => [
          group.id,
          {
            productId: priceChangeItems.product.productId,
            group,
            uomId: selectedUom?.id ?? "",
            priceChangeType,
            assignedToId: priceChangeItems.product.classification?.productManager?.userId,
          },
        ])
      )
    }, [
      persistEdits,
      priceChangeItems.groups,
      priceChangeItems.product.productId,
      priceChangeItems.product.classification?.productManager?.userId,
      selectedUom?.id,
      priceChangeType,
    ])

    const handleUomChange = (id: string) => {
      const nextSelectedUom = uoms.find((uom) => uom.id === id)
      setSelectedUom(nextSelectedUom)
    }

    return (
      <div className='w-full'>
        <PricingProductHeader
          product={priceChangeItems.product}
          quoteProduct={quoteProduct}
          uoms={uoms}
          selectedUom={selectedUom}
          onUomChange={handleUomChange}
          onAddSpecialPriceClick={() => handleAddPackageClick(PricingDialogs.AddSpecialPrice)}
          onAddCostPackageClick={() => handleAddPackageClick(PricingDialogs.AddCostPackage)}
          onRemoveSubmissionProduct={handleRemoveSubmissionProduct}
          readonly={readonly}
          variant={variant}
          priceChangeType={priceChangeType}
          canRemove={canRemove}
        />
        {hasPriceChangeGroupsWithItems(priceChangeItems) && (
          <>
            {priceChangeItems.groups.map((group) => (
              <div key={group.id}>
                <PricingWarehouseHeader
                  group={group}
                  priceChangeType={priceChangeType}
                  readonly={readonly}
                  variant={variant}
                  hasSelectedRows={(groupSelectedCounts[group.id] ?? 0) > 0}
                  onPriceChangeApply={(values) => handleWarehousePriceChangeApply(group.id, values)}
                  onCostChangeApply={(values) => handleWarehouseCostChangeApply(group.id, values)}
                  onAcceptedCostChange={(value) => handleGroupNewAcceptedCostChange(group.id, value)}
                />

                <PricingTiersTable
                  ref={(el: PricingTiersTableHandle | null) => {
                    tiersTableRefs.current[group.id] = el
                  }}
                  uomName={selectedUom?.name ?? ""}
                  initialRows={initialRowsByGroup[group.id] ?? []}
                  isUomChanged={isUomChanged}
                  isQuoteSource={isQuoteSource}
                  readonly={readonly}
                  variant={variant}
                  priceChangeType={priceChangeType}
                  onSelectionChange={(info) => handleGroupSelectionChange(group.id, info)}
                  onChangesChange={(hasChanges) => handleGroupChangesChange(group.id, hasChanges)}
                  editContext={editContextByGroup[group.id]}
                  hasBulkPreview={Boolean(bulkUpdate)}
                />
              </div>
            ))}
          </>
        )}
        {pendingWarehouseUpdate && (
          <ConfirmationDialog
            title='Overwrite manual edits?'
            content={
              pendingWarehouseUpdate.kind === "cost"
                ? "Some rows in this group have been manually edited. Applying the cost change will overwrite those values."
                : "Some selected rows have been manually edited. Applying changes will overwrite those values."
            }
            closeLabel='Cancel'
            confirmLabel='Apply anyway'
            isWarning
            onClose={() => setPendingWarehouseUpdate(null)}
            onConfirm={() => handleWarehouseUpdateApply(pendingWarehouseUpdate)}
          />
        )}
        {pendingAddPackageDialog && (
          <ConfirmationDialog
            title='Discard manual edits?'
            content="Adding a package will discard any manual edits made to this product's pricing. This action cannot be undone."
            closeLabel='Cancel'
            confirmLabel='Add Package'
            isWarning
            onClose={() => setPendingAddPackageDialog(null)}
            onConfirm={handleAddPackageConfirm}
          />
        )}
      </div>
    )
  }
)

ProductPricingTable.displayName = "ProductPricingTable"
